import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardPlus, Search, Upload, X, Loader2, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DynamicFields from "@/components/DynamicFields";
import { ISSUE_TYPES, CLAIM_TYPES } from "@/lib/constants";
import api from "@/lib/apiClient";

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}

export default function NewClaim() {
  const navigate = useNavigate();
  const [serial, setSerial] = useState("");
  const [battery, setBattery] = useState(null);
  const [looking, setLooking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "", customer_type: "B2C",
    claim_type: "Replace", address: "", city: "", pincode: "", purchase_date: "", invoice_no: "",
    issue_type: "", issue_description: "",
  });
  const [custom, setCustom] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setCustomField = (k, v) => setCustom((c) => ({ ...c, [k]: v }));

  const lookup = async () => {
    if (!serial) return;
    setLooking(true);
    try {
      const res = await api.get(`/batteries/lookup/${serial}`);
      const b = res.data;
      setBattery(b);
      setForm((f) => ({
        ...f,
        customer_name: b.customer_name || f.customer_name,
        customer_phone: b.customer_phone || f.customer_phone,
        customer_email: b.customer_email || f.customer_email,
        customer_type: b.channel || f.customer_type,
        city: b.customer_city || f.city,
        pincode: b.customer_pincode || f.pincode,
        purchase_date: b.purchase_date || f.purchase_date,
        invoice_no: b.invoice_no || f.invoice_no,
        address: b.customer_address || f.address,
      }));
      toast.success("Battery found — details prefilled.");
    } catch (err) {
      setBattery(null);
      toast.error(err.response?.data?.detail || "Battery not found.");
    } finally {
      setLooking(false);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, res.data]);
      }
      toast.success("Document uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!serial || !form.customer_name || !form.customer_phone || !form.issue_type || !form.issue_description) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (attachments.length === 0) {
      toast.error("Please upload at least one supporting document (invoice/photo).");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, battery_serial: serial, attachments: attachments.map((a) => a.path), custom };
      const res = await api.post("/claims", payload);
      toast.success(`Claim ${res.data.claim_no} submitted!`);
      navigate(`/claims/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit claim.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="new-claim-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
          <ClipboardPlus className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">New Warranty Claim</h1>
          <p className="text-muted-foreground">Raise a warranty claim for a registered battery.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Battery & Claim Type</h3>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div className="flex gap-2 items-end">
              <Field label="Battery Serial Number" required>
                <Input data-testid="claim-serial-input" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="GRB1000" />
              </Field>
              <Button type="button" variant="secondary" onClick={lookup} disabled={looking} className="gap-2" data-testid="lookup-button">
                {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Lookup
              </Button>
            </div>
            <Field label="Type of Claim" required>
              <Select value={form.claim_type} onValueChange={set("claim_type")}>
                <SelectTrigger data-testid="claim-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CLAIM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {battery && (
            <div className="mt-3 text-sm rounded-lg bg-accent/50 p-3 text-accent-foreground" data-testid="battery-found-info">
              <span className="font-semibold">{battery.model}</span> · {battery.capacity} · Warranty ends {battery.warranty_end}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Customer Information</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" required><Input data-testid="claim-name-input" value={form.customer_name} onChange={set("customer_name")} /></Field>
            <Field label="Phone" required><Input data-testid="claim-phone-input" value={form.customer_phone} onChange={set("customer_phone")} /></Field>
            <Field label="Email"><Input type="email" value={form.customer_email} onChange={set("customer_email")} /></Field>
            <Field label="Customer Type">
              <Select value={form.customer_type} onValueChange={set("customer_type")}>
                <SelectTrigger data-testid="claim-customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="B2C">B2C</SelectItem>
                  <SelectItem value="B2B">B2B</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="City"><Input value={form.city} onChange={set("city")} /></Field>
            <Field label="Pincode"><Input value={form.pincode} onChange={set("pincode")} /></Field>
            <Field label="Address"><Input value={form.address} onChange={set("address")} /></Field>
            <Field label="Invoice No."><Input value={form.invoice_no} onChange={set("invoice_no")} /></Field>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Issue Details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Issue Type" required>
              <Select value={form.issue_type} onValueChange={set("issue_type")}>
                <SelectTrigger data-testid="issue-type-select"><SelectValue placeholder="Select issue" /></SelectTrigger>
                <SelectContent>{ISSUE_TYPES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Purchase Date"><Input type="date" value={form.purchase_date} onChange={set("purchase_date")} /></Field>
          </div>
          <div className="mt-4">
            <Field label="Describe the problem" required>
              <Textarea data-testid="issue-desc-input" value={form.issue_description} onChange={set("issue_description")} rows={4} placeholder="Explain the issue you are facing..." />
            </Field>
          </div>

          <div className="mt-4">
            <Label className="text-sm">Supporting Documents <span className="text-red-500">*</span> <span className="text-xs text-muted-foreground font-normal">(invoice / photos — required)</span></Label>
            <label className="mt-1.5 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-slate-50 hover:bg-accent/40 transition-colors p-6 cursor-pointer" data-testid="upload-dropzone">
              {uploading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground mt-2">Click to upload images or PDF</span>
              <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={onUpload} data-testid="file-input" />
            </label>
            {attachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs" data-testid={`attachment-chip-${i}`}>
                    {a.filename}
                    <button type="button" onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <DynamicFields entity="claim" values={custom} onChange={setCustomField} />
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="gap-2" data-testid="submit-claim-button">
            <Send className="h-4 w-4" /> {saving ? "Submitting..." : "Submit Claim"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
